import { Alert, Button, Col, Divider, Form, Input, Modal, Row, Space, Switch, Typography } from "antd"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import appConfig from "../../../appConfig"
import Icon from "../../../assets/icons/icon"
import CardBox from "../../../components/cardBox"
import CopyableContent from "../../../components/copyableContent"
import DateSelect from "../../../components/form/dateSelect"
import EnumRadio from "../../../components/form/EnumRadio"
import InputNum from "../../../components/form/inputNum"
import InputVin from "../../../components/form/inputVin"
import DividerLine from "../../../components/lineSplit"
import { renderErrorMessage } from "../../../components/messages/errorMessage"
import ServerAutoFill from "../../../components/serverAutoFill"
import ServerSelectBox from "../../../components/serverSelectBox"
import UnmatchedValueWarning from "../../../components/unmatchedValueWarning"
import { useTaxLegalActions, useTaxLegalEvaluation, useTaxLegalVehicle } from "../../../contexts/taxLegalCaseStore"
import _t from "../../../lang/translate"
import { getPath } from "../../../routes/appRoutes"
import { getSavedEnums } from "../../../services/authService"
import { brandsAutoComplete, dealersAutoComplete, modelsAutoComplete, reporterCompaniesAutoComplete } from "../../../services/autocompleteService"
import http from "../../../services/httpService"
import { getTaxCase, taxCasesURL } from "../../../services/taxService"
import { statusKey, vinKey } from "../../../services/urlQueryService"
import { PaginationedData } from "../../../types/apiTypes"
import { IdType, RemoteVehicleData, Vehicle } from "../../../types/appTypes"
import { EditVehicleAndEvaluation, OriginalEvaluation } from "../../../types/taxLegalTypes"
import { TaxCase, TaxListItem } from "../../../types/taxTypes"
import consts from "../../../utilities/consts"
import format from "../../../utilities/formatNumbers"
import { isValidVin } from "../../../utilities/stringExtraFunctions"
import { PriceSourceTags, SelectNewPriceSource } from "../shared"

type Props = {
  vehicle: Vehicle | null
}
export default function TaxLegalVehicleCard({ vehicle }: Props) {
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  function editVehicle() {
    setEditVehicleOpen(true)
  }

  return (
    <>
      <Space direction="vertical" size={"small"}>
        <Space>
          <Typography.Title level={3} ellipsis className="mb-0">
            {vehicle?.brand ? (
              <>
                {vehicle?.brand} {vehicle?.model}
              </>
            ) : (
              "-"
            )}
          </Typography.Title>
          <Button onClick={editVehicle} type="link" icon={<Icon name="pencil-simple" size="large" />} />
        </Space>
        <Space
          wrap
          style={{ rowGap: 0, marginTop: 4 }}
          split={<Divider type="vertical" className="ml-05 mr-05" />}
        >
          {vehicle?.firstRegDate && (
            <CopyableContent className="text-medium" contentToCopy={format.date(vehicle?.firstRegDate)}>
              {format.date(vehicle?.firstRegDate)}
            </CopyableContent>
          )}
          {vehicle?.vin && (
            <CopyableContent className="text-medium" contentToCopy={vehicle?.vin ?? ""}>
              {vehicle?.vin}
            </CopyableContent>
          )}
          {vehicle?.mileage && (
            <CopyableContent className="text-medium" contentToCopy={String(vehicle?.mileage ?? "")}>
              {format.milage(vehicle?.mileage)}
            </CopyableContent>
          )}
        </Space>
      </Space>
      <EditVehicleAndAssessment isOpen={editVehicleOpen} close={() => setEditVehicleOpen(false)} />
    </>
  )
}

type EditVehicleAndEvaluationProps = {
  isOpen: boolean;
  close: () => void;
};
function EditVehicleAndAssessment({ isOpen, close }: EditVehicleAndEvaluationProps) {
  const vehicle = useTaxLegalVehicle();
  const evaluation = useTaxLegalEvaluation();
  const { setVehicle, setEvaluation } = useTaxLegalActions();
  const [form] = Form.useForm<EditVehicleAndEvaluation>();
  const [vinCompletedInCase, setVinCompletedInCase] = useState<IdType[]>([]);
  const [loading, setLoading] = useState(false);

  function applyChanges(values: EditVehicleAndEvaluation) {
    setVehicle({
      ...values.vehicle,
    });

    let reporterCompany = values?.originalEvaluation?.reporterCompany;
    if (reporterCompany) {
      reporterCompany = reporterCompany.split("(")[0]?.trim()
    }
    setEvaluation({
      ...values.originalEvaluation,
      reporterCompany,
    });
    close();
  }

  function handleVinFetch(data: RemoteVehicleData) {
    const dmr = data.dmr;
    form.setFieldsValue({
      vehicle: {
        vin: dmr?.mapped?.vin,
        brand: dmr?.mapped?.brand,
        model: dmr?.mapped?.model,
        firstRegDate: dmr?.mapped?.firstRegDate,
        mileage: dmr?.mapped?.mileage,
      },
    });
  }

  async function handleFetchTaxCase() {
    try {
      setLoading(true);
      const taxCaseId = form.getFieldValue(["originalEvaluation", "dmfCaseId"]);
      if (!taxCaseId) return;
      const { data: taxCase } = await getTaxCase(taxCaseId);
      const reporterCompany = form.getFieldValue(["originalEvaluation", "reporterCompany"]);
      const result = taxCase.scenarios.optimization?.calculation
      const { equipmentPriceSourceTags, newPriceSourceTag } = getPriceSourcesFromTaxCase(taxCase)
      form.setFieldsValue({
        originalEvaluation: {
          // NOTE: caseHandler: This field exists basically as a data-field for the server and the database,
          // such that we can look it up in the future. We will not display it in the frontend.
          caseHandler: taxCase.log?.receipt?.person.fullName,
          reporterCompany: reporterCompany ?? appConfig("companyName"),
          reviewDate: taxCase.log?.receipt?.date || null,
          registrationFee: result?.registrationFee?.registrationFee,
          equipmentsPrice: result?.newPrice?.equipmentPrice,
          newPrice: result?.newPrice?.newPrice,
          tradePrice: result?.tradePrice?.tradePrice,
          newPriceSourceTag,
          equipmentPriceSourceTags
        },
        vehicle: {
          ...taxCase.vehicle,
        },
      });
      if (taxCase.vehicle?.vin) {
        searchCompletedTaxCasesMatchingVin(taxCase.vehicle.vin);
      }
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      setLoading(false);
    }
  }
  const initialValues = { vehicle: { ...vehicle }, originalEvaluation: { ...evaluation } };
  if (!initialValues.vehicle.typeId) {
    initialValues.vehicle.typeId = 1;
  }

  function handleBrandIdChange(_value: any, option: { label?: string } | any) {
    if (option.label) {
      form.setFieldsValue({ vehicle: { brand: option.label } });
    }
  }

  async function searchCompletedTaxCasesMatchingVin(vin: string) {
    try {
      const query = new URLSearchParams();
      const completedStatusId = getSavedEnums()?.status.find(
        (status) => status.title === "Gennemført",
      )?.id;
      query.set(vinKey, vin);
      if (completedStatusId) query.set(statusKey, String(completedStatusId));
      const url = taxCasesURL(query.toString());
      const { data } = await http.get<PaginationedData<TaxListItem>>(url);
      setVinCompletedInCase(data.data.map((taxCase) => taxCase.id));
    } catch (error) {
      renderErrorMessage(error);
    }
  }
  function handleValuesChange(
    changedValues: Partial<EditVehicleAndEvaluation>,
    values: EditVehicleAndEvaluation,
  ) {
    if (changedValues.vehicle?.vin && isValidVin(changedValues.vehicle.vin)) {
      searchCompletedTaxCasesMatchingVin(changedValues.vehicle.vin);
    }
  }

  useEffect(() => {
    if (vehicle?.vin) {
      searchCompletedTaxCasesMatchingVin(vehicle.vin);
    }
  }, []);

  return (
    <Modal
      title={_t("editVehicleAndOriginalValuation")}
      visible={isOpen}
      onCancel={close}
      width={900}
      footer={null}
    >
      <Form<EditVehicleAndEvaluation>
        form={form}
        {...consts.formItemProps}
        className="m-0 form-items-mb-05"
        requiredMark={false}
        onFinish={applyChanges}
        onValuesChange={handleValuesChange}
        initialValues={initialValues}
      >
        <Row gutter={[32, 24]}>
          <Col span={24}>
            <Form.Item
              label={_t("tax", "id").toUpperCase()}
              name={["originalEvaluation", "dmfCaseId"]}
              rules={[{ pattern: /\d+/, message: "TAX ID er ikke et nummer" }]}
            >
              <Input
                disabled={loading}
                placeholder="82545"
                className="input-group-btn"
                addonAfter={
                  <Button loading={loading} onClick={handleFetchTaxCase}>
                    Hent sag
                  </Button>
                }
              />
            </Form.Item>
            <DividerLine />
          </Col>
          <Col lg={{ span: 12 }} span={24}>
            <Form.Item name={["vehicle", "typeId"]} label={_t("vehicle_type")}>
              <EnumRadio size="small" nameKey={["vehicle", "type"]} />
            </Form.Item>

            <Form.Item label={_t("vin")} name={["vehicle", "vin"]}>
              <InputVin useRemoteVehicleData onFetched={handleVinFetch} />
            </Form.Item>

            <Form.Item hidden name={["vehicle", "brand"]}>
              <Input />
            </Form.Item>

            <Form.Item
              shouldUpdate={(curr, next) => {
                const currData = curr.vehicle.typeId;
                const nextData = next.vehicle.typeId;
                return currData !== nextData;
              }}
              noStyle
            >
              {({ getFieldValue, setFieldsValue }) => {
                const typeId = getFieldValue(["vehicle", "typeId"]);
                const url = brandsAutoComplete("", "", typeId);
                return (
                  <Form.Item
                    rules={[{ required: true }]}
                    name={["vehicle", "brandId"]}
                    label={_t("brand")}
                  >
                    <ServerSelectBox onChange={handleBrandIdChange} apiUrl={url} />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              shouldUpdate={(curr, next) => {
                const currDataBrand = curr.vehicle.brandId;
                const currDataType = curr.vehicle.typeId;
                const nextDataBrand = next.vehicle.brandId;
                const nextDataType = next.vehicle.typeId;
                return (
                  currDataBrand !== nextDataBrand ||
                  currDataType !== nextDataType
                );
              }}
              noStyle
            >
              {({ getFieldsValue }) => {
                const data = getFieldsValue();
                const { brandId, typeId } = data?.vehicle || {};

                const url = modelsAutoComplete("", brandId, "", typeId);
                return (
                  <Form.Item
                    rules={[{ required: true }]}
                    name={["vehicle", "model"]}
                    label={_t("model")}
                  >
                    <ServerAutoFill apiUrl={url} disabled={!brandId || !typeId} />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item label={_t("firstRegDate")} name={["vehicle", "firstRegDate"]}>
              <DateSelect />
            </Form.Item>
            <Form.Item className="mb-2" label={_t("mileage")} name={["vehicle", "mileage"]}>
              <InputNum />
            </Form.Item>

            <Form.Item
              valuePropName="checked"
              className="switch-form-item-flex-right"
              label={_t("mileage_documentation")}
              name={["originalEvaluation", "mileageDocumentation"]}
            >
              <Switch />
            </Form.Item>
            <SelectNewPriceSource path={["originalEvaluation"]} />
            <PriceSourceTags path={["originalEvaluation"]} />

          </Col>
          <Col lg={{ span: 12 }} span={24}>
            {!!vinCompletedInCase.length && (
              <Alert
                className="mb-1"
                showIcon
                type="warning"
                closable
                message={
                  <Space size={"small"}>
                    Stelnummeret er værdifastsat i TAX-sagsnummer{" "}
                    {vinCompletedInCase.map((id) => (
                      <Link key={id} target={"_blank"} to={getPath("tax", id)}>
                        #{id}
                      </Link>
                    ))}
                  </Space>
                }
              />
            )}
            <CardBox
              className="pt-1"
              title={
                <Typography.Title className="mb-1" level={5}>
                  {_t("original", "evaluation")}
                </Typography.Title>
              }
            >
              <Form.Item label={_t("reviewDate")} name={["originalEvaluation", "reviewDate"]}>
                <DateSelect />
              </Form.Item>

              <Form.Item
                label={_t("selfReporter")}
                name={["originalEvaluation", "reporterCompany"]}
                rules={[{ required: true }]}
              >
                <ServerAutoFill placeholder={_t("choose", "reporter")} apiUrl={reporterCompaniesAutoComplete()} />
              </Form.Item>

              <Form.Item hidden name={["originalEvaluation", "caseHandler"]} >
                <Input />
              </Form.Item>

              <DividerLine style={{ marginBlock: 12 }} />
              <Form.Item label={_t("new_price")} name={["originalEvaluation", "newPrice"]}>
                <InputNum />
              </Form.Item>
              <Form.Item
                label={_t("equipment_new_price")}
                name={["originalEvaluation", "equipmentsPrice"]}
              >
                <InputNum />
              </Form.Item>
              <Form.Item label={_t("trade_price")} name={["originalEvaluation", "tradePrice"]}>
                <InputNum />
              </Form.Item>
              <Form.Item
                label={_t("registration_fee")}
                name={["originalEvaluation", "registrationFee"]}
              >
                <InputNum />
              </Form.Item>
            </CardBox>
          </Col>
        </Row>

        {/* FOOTER */}
        <Space
          style={{ width: "100%", justifyContent: "space-between", paddingTop: 24, marginTop: 24 }}
        >
          <Button onClick={close}>{_t("close")}</Button>
          <Button type="primary" htmlType="submit">
            {_t("apply")}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
}

function getPriceSourcesFromTaxCase(taxCase: TaxCase): Pick<OriginalEvaluation, 'newPriceSourceTag' | 'equipmentPriceSourceTags'> {
  const manager = taxCase.scenarios.optimization?.calculation?.newPrice?.manager?.data;
  const newPrice = manager?.newPrices?.find(p => p.id === manager.selectedNewPrice)
  const equipment = taxCase.scenarios.optimization?.calculation?.newPrice?.manager?.data?.equipment;
  const equipmentSourcesMap: Record<string, { source: string, sourceId: number | null }> = {}
  for (const eq of equipment ?? []) {
    if (!(eq.mounted || eq.isForceMounted)) continue;
    if (eq.includedInNewPrice || eq.isIncludedInMountedPackage) continue;
    if (eq.source) equipmentSourcesMap[eq.source] = { source: eq.source, sourceId: eq.sourceId }
  }
  const equipmentSources = Object.values(equipmentSourcesMap)
  const equipmentPriceSourceTags = equipmentSources.map(s => mapManagerSource(s.source, s.sourceId).id?.toString()).filter(Boolean) as string[]
  const newPriceSourceTag = mapManagerSource(newPrice?.source ?? null, newPrice?.sourceId ?? null).id

  return {
    newPriceSourceTag,
    equipmentPriceSourceTags,
  }
}

function mapManagerSource(source: string | null, sourceId: number | null): { id: number | null } {
  let id = null //label = null;
  if (source === 'Custom' || source === 'Brugerdefineret' || sourceId === 1) {
    id = 40; // label = null;
  } else if (source === 'DAT' || sourceId === 2) {
    id = 26; // label = "DAT.de";
  } else if (source === 'DMR-Afgørelse' || source === 'Motor Agency' || sourceId === 3) {
    id = 41; // label = null;
  } else if (source === 'Importør' || source === 'Importer' || sourceId === 4) {
    id = 31; // label = "Prisliste";
  } else if (source === 'Estimatic' || sourceId === 5) {
    id = 27; // label = "Estimatic";
  } else if (source === 'Bilinfo' || sourceId === 6) {
    id = 25; // label = "Bilinfo";
  } else if (source === 'Standardpriser' || sourceId === 7) {
    id = 42; // label = null;
  }

  return { id }
}
